import * as React from 'react';
import { Link } from 'react-router-dom';

import * as Yup from 'yup';

import Button from '@common/react/components/Forms/Button';
import Captcha from '@common/react/components/Forms/Captcha';
import { simpleStringValidator } from '@common/react/utils/validationHelpers';
import FormikField from '@common/react/components/Forms/FormikField/FormikField';
import PhoneOrEmailInput, { emailOrPhoneValidator } from '@common/react/components/Forms/PhoneOrEmailInput/PhoneOrEmailInput';
import SetPassword from '@common/react/components/Pages/Recover/SetPassword';
import ServerPageProvider from '@common/react/components/Core/ServerPageProvider/ServerPageProvider';
import { ItemProvider } from '@common/react/components/Core/ItemProvider/ItemProvider';
import { ItemEditor } from '@common/react/components/Core/ItemEditor/ItemEditor';
import { WithId } from '@common/typescript/objects/WithId';
import { RequestButton } from '@common/react/components/UI/LoadingButton/LoadingButton';
import { BaseUser } from '@common/typescript/objects/BaseUser';

import '@common/react/scss/components/recover.scss';

interface FormValues extends WithId {
	info: string;
	captcha: string;
	code: string;
}

interface Props {
	path?: string;
	loginPath?: string;
	info?: boolean;
	logo?: React.ReactNode;
	containerClassName?: string;
	withFieldTitle?: boolean;
	withResend?: boolean;
	recoverByPhoneRequest?: string;
	recoverByEmailRequest?: string;
	setPasswordByPhoneRequest?: string;
	resendSetPasswordByPhoneCodeRequest?: string;
}

const validationSchema = Yup.object().shape({
	info: emailOrPhoneValidator,
	captcha: simpleStringValidator,
});

const Recover: <User extends BaseUser>(p: Props) => React.ReactElement<User> = <User extends BaseUser, >(props) => {
	const {
		path = '/recover',
		loginPath = '/login',
		info = 'Enter the email or phone number you provided during registration'
			+ ' and we will send you a password reset email or confirmation code',
		logo,
		containerClassName = 'recover-page',
		withFieldTitle,
		withResend = false,
		recoverByPhoneRequest = 'recoverByPhone',
		recoverByEmailRequest = 'recover',
		setPasswordByPhoneRequest,
		resendSetPasswordByPhoneCodeRequest,
	} = props;
	const [success, setSuccess] = React.useState<boolean>(false);
	const [counter, setCounter] = React.useState<number>(0);
	const [phoneNumber, setPhoneNumber] = React.useState('');
	const [visibleConfirm, setVisibleConfirm] = React.useState(false);
	const [savedValues, setSavedValues] = React.useState<FormValues>();

	const innerRender = () => {
		if (success) {
			return <div className="enter-page__form general-form">
				{visibleConfirm ? <SetPassword<User>
					recoverPath={path}
					phoneNumber={phoneNumber}
					withFieldTitle={withFieldTitle}
					setPasswordByPhoneRequest={setPasswordByPhoneRequest}
					resendSetPasswordByPhoneCodeRequest={resendSetPasswordByPhoneCodeRequest}
					onBack={() => {
						setSuccess(false);
						setPhoneNumber('');
						setVisibleConfirm(false);
					}}
				/> : <div className="text-center">
					<div><i className="fa fa-envelope" style={{ fontSize: 70, marginBottom: 30 }} /></div>
					<h1 style={{ fontWeight: 'bold', fontSize: 35 }}>Email confirmation</h1>
					<h4 style={{ fontSize: 16, lineHeight: 1.4 }}>
						We have sent email to
						{' '}
						<span style={{ color: '#33c088' }}>{savedValues?.info}</span>
						&nbsp;to confirm the validity of email address. After receiving the email follow
						the link provided to complete recover.
					</h4>
					{withResend && <>
						<hr style={{ padding: '0 10px', margin: '30px 30px 20px' }} />
						<h4 style={{ fontSize: 16, lineHeight: 1.4 }}>
							If you not get any mail
							{' '}
							<RequestButton
								className="recover-page__resend"
								beforeAction={() => {
									return !!(success && savedValues && savedValues.info.includes('@'));
								}}
								requestType={recoverByEmailRequest}
								requestProps={{ email: savedValues?.info }}
								successMessage="The confirmation email has been successfully sent."
							>
								Resend confirmation mail
							</RequestButton>
						</h4>
					</>}
				</div>}
			</div>;
		}

		return (
			<div>
				<div className="enter-page__form general-form">
					<div className="site-headline text-center">
						<h1>Recover password</h1>
					</div>
					<p className="text-center">
						{info}
					</p>
					<ItemProvider<FormValues>
						id={-1}
						type=""
						validationSchema={validationSchema}
						add={{
							info: '',
							captcha: '',
							code: '',
						}}
						transformAfterSave={() => ({
							info: '',
							captcha: '',
							code: '',
						})}
						clearForSubmit={(values) => {
							return values.info.includes('@') ? {
								email: values.info,
								captcha: values.captcha,
								code: values.code,
							} : {
								phoneNumber: values.info,
								captcha: values.captcha,
								code: values.code,
							};
						}}
						onRequestError={() => setCounter((prev) => prev + 1)}
					>
						<ItemEditor<FormValues>
							successMessage=""
							afterSubmit={() => {
								setSuccess(true);
								setCounter((prev) => prev + 1);
							}}
							resetFormAfterSubmit
							beforeSubmit={(values, actions, submit) => {
								return submit()
									.then(() => {
										const isEmail = values.info.includes('@');
										setVisibleConfirm(!isEmail);
										setPhoneNumber(!isEmail ? values.info : '');
										setSavedValues(values);
									});
							}}
							getRequestName={(values) => (values.info.includes('@') ? recoverByEmailRequest : recoverByPhoneRequest)}
							edit={(formikBag, _, { loading }) => (
								<>
									<FormikField
										fieldName="info"
										title={withFieldTitle ? 'Email or Phone Number*' : ''}
										containerClassName="form-group"
										render={(fieldProps, inputProps) =>
											<PhoneOrEmailInput
												fieldProps={fieldProps}
												className="form-control"
												placeholder={withFieldTitle ? '' : 'Email or Phone Number*'}
												{...inputProps}
											/>}
									/>
									<FormikField
										fieldName="captcha"
										containerClassName="form-group"
										title={withFieldTitle ? 'Bot Protection Code*' : ''}
										render={({ field }) => <div className="input-with-captcha">
											<Captcha
												iconClassName="fa fa-refresh"
												update={(value) => formikBag.setFieldValue('code', value)}
												counter={counter}
											/>
											<input
												className="form-control"
												id="captcha"
												placeholder={withFieldTitle ? '' : 'Bot Protection Code*'}
												{...field}
											/>
										</div>}
									/>
									<div className="clearfix mb10">
										<Link className="pull-right" to={loginPath}>Back</Link>
									</div>
									<div className="text-center">
										<Button className="btn btn-primary" isLoading={loading}>Recover</Button>
									</div>
								</>
							)}
						/>
					</ItemProvider>
				</div>
			</div>
		);
	};

	return (
		<div className={`recover-page__main ${containerClassName}`}>
			<ServerPageProvider
				path="recover"
				defaultTitle="Recover"
				loader={<></>}
				inner={(serverPage) => <></>}
			/>
			{logo}
			<div className="enter-page__container container">
				{innerRender()}
			</div>
		</div>
	);
};

export default Recover;
